import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Switch,
  Typography,
  Alert,
} from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Radio, MapPin } from 'lucide-react';
import { useBookingSocket } from '../hooks/useBookingSocket';

// Leaflet's default marker icons don't resolve under bundlers; point them at
// the CDN-free data the package ships.
const markerIcon = new L.Icon({
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

// Keep the map centred on the moving handler.
function Recenter({ position }) {
  const map = useMap();
  if (position) map.setView(position, map.getZoom(), { animate: true });
  return null;
}

/**
 * Live location panel for an in-progress service.
 * - Handler: a switch to share/stop sharing their live location.
 * - Owner: a live map that follows the handler while they're sharing.
 */
const LiveTracking = ({ bookingId, isHandler, canShare = true }) => {
  const { connectionState, trackingActive, handlerLocation, setSharing } =
    useBookingSocket(bookingId, { enabled: true, isHandler });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleToggle = async (e) => {
    const next = e.target.checked;
    try {
      setBusy(true);
      setError(null);
      await setSharing(next);
    } catch (err) {
      setError(err.message || 'Could not update location sharing.');
    } finally {
      setBusy(false);
    }
  };

  const liveChip = trackingActive ? (
    <Chip
      icon={<Radio size={14} />}
      label="Live"
      color="success"
      size="small"
      sx={{ '& .MuiChip-icon': { animation: 'pulse 1.5s infinite' } }}
    />
  ) : (
    <Chip label="Not sharing" size="small" variant="outlined" />
  );

  // ─── Handler view: the on/off control ───
  if (isHandler) {
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MapPin size={18} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Live location
              </Typography>
            </Box>
            {liveChip}
          </Box>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={trackingActive}
                onChange={handleToggle}
                disabled={busy || !canShare}
              />
            }
            label={
              trackingActive
                ? 'Sharing your location with the owner'
                : 'Share your live location (e.g. during a walk)'
            }
          />
          {!canShare && (
            <Typography variant="caption" color="text.secondary" display="block">
              You can share location once the service is in progress.
            </Typography>
          )}
          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </CardContent>
      </Card>
    );
  }

  // ─── Owner view: the live map ───
  const position = handlerLocation
    ? [handlerLocation.latitude, handlerLocation.longitude]
    : null;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MapPin size={18} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Handler location
            </Typography>
          </Box>
          {liveChip}
        </Box>

        {position ? (
          <Box sx={{ height: 300, borderRadius: 1, overflow: 'hidden' }}>
            <MapContainer center={position} zoom={16} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                attribution='&copy; OpenStreetMap contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={position} icon={markerIcon}>
                <Popup>Your handler is here</Popup>
              </Marker>
              <Recenter position={position} />
            </MapContainer>
          </Box>
        ) : (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {trackingActive
                ? 'Waiting for the first location update…'
                : 'Your handler is not sharing their location right now.'}
            </Typography>
          </Box>
        )}

        {connectionState !== 'connected' && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {connectionState === 'connecting' ? 'Connecting…' : 'Reconnecting…'}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default LiveTracking;
